import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { Navbar } from "@/components/Navbar";
import { CookieBanner } from "@/components/CookieBanner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AdminLogin from "@/pages/AdminLogin";
import PasswordResetRequest from "@/pages/PasswordResetRequest";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CustomerEvents from "@/pages/CustomerEvents";
import CustomerReceipts from "@/pages/CustomerReceipts";
import CustomerCoupons from "@/pages/CustomerCoupons";
import CustomerProfile from "@/pages/CustomerProfile";
import JoinedBusinesses from "@/pages/JoinedBusinesses";
import JoinedEvents from "@/pages/JoinedEvents";
import Notifications from "@/pages/Notifications";
import CustomerWishlist from "@/pages/CustomerWishlist";
import JoinBusinessScan from "@/pages/JoinBusinessScan";
import JoinEventScan from "@/pages/JoinEventScan";
import BusinessDashboard from "@/pages/BusinessDashboard";
import BusinessProfile from "@/pages/BusinessProfile";
import PromotionalEmail from "@/pages/PromotionalEmail";
import SubscriberPools from "@/pages/SubscriberPools";
import ReceiptApproval from "@/pages/ReceiptApproval";
import EventDetails from "@/pages/EventDetails";
import DrawExecution from "@/pages/DrawExecution";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSettings from "@/pages/AdminSettings";
import AdminLogs from "@/pages/AdminLogs";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import FAQ from "@/pages/FAQ";
import Careers from "@/pages/Careers";
import CookiePreferences from "@/pages/CookiePreferences";
import "@/App.css";

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRole = localStorage.getItem('role');
    if (storedUser && storedRole) {
      setUser(JSON.parse(storedUser));
      setRole(storedRole);
    }
    setLoading(false);
  }, []);

  const handleAuthSuccess = (userData, userRole) => {
    setUser(userData);
    setRole(userRole);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    setUser(null);
    setRole(null);
  };

  const ProtectedRoute = ({ children, allowedRole }) => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="font-outfit text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }
    
    if (!user) {
      return <Navigate to="/login" />;
    }
    if (allowedRole && role !== allowedRole) {
      return <Navigate to="/" />;
    }
    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Navbar user={user} role={role} onLogout={handleLogout} />
        <Toaster position="top-center" richColors />
        <CookieBanner />
        <Routes>
          <Route 
            path="/" 
            element={
              user ? (
                role === 'customer' ? <Navigate to="/customer/events" /> :
                role === 'business' ? <Navigate to="/business/dashboard" /> :
                role === 'admin' ? <Navigate to="/admin/dashboard" /> :
                <Landing />
              ) : (
                <Landing />
              )
            } 
          />
          <Route path="/login" element={<Auth onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/register" element={<Auth onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/admin/login" element={<AdminLogin onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/password-reset" element={<PasswordResetRequest />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Public QR Scan Routes */}
          <Route path="/join/business/:businessId" element={<JoinBusinessScan />} />
          <Route path="/join/event/:eventId" element={<JoinEventScan />} />
          
          {/* Public About Pages */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/cookie-preferences" element={<CookiePreferences />} />
          
          {/* Customer Routes */}
          <Route
            path="/customer/events"
            element={
              <ProtectedRoute allowedRole="customer">
                <CustomerEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/receipts"
            element={
              <ProtectedRoute allowedRole="customer">
                <CustomerReceipts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/coupons"
            element={
              <ProtectedRoute allowedRole="customer">
                <CustomerCoupons />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/joined-businesses"
            element={
              <ProtectedRoute allowedRole="customer">
                <JoinedBusinesses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/joined-events"
            element={
              <ProtectedRoute allowedRole="customer">
                <JoinedEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/profile"
            element={
              <ProtectedRoute allowedRole="customer">
                <CustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/notifications"
            element={
              <ProtectedRoute allowedRole="customer">
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/wishlist"
            element={
              <ProtectedRoute allowedRole="customer">
                <CustomerWishlist />
              </ProtectedRoute>
            }
          />
          
          {/* Business Routes */}
          <Route
            path="/business/dashboard"
            element={
              <ProtectedRoute allowedRole="business">
                <BusinessDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/profile"
            element={
              <ProtectedRoute allowedRole="business">
                <BusinessProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/promotional-email"
            element={
              <ProtectedRoute allowedRole="business">
                <PromotionalEmail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/subscriber-pools"
            element={
              <ProtectedRoute allowedRole="business">
                <SubscriberPools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/receipts"
            element={
              <ProtectedRoute allowedRole="business">
                <ReceiptApproval />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/events/:eventId"
            element={
              <ProtectedRoute allowedRole="business">
                <EventDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/events/:eventId/draw"
            element={
              <ProtectedRoute allowedRole="business">
                <DrawExecution />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRole="admin">
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <ProtectedRoute allowedRole="admin">
                <AdminLogs />
              </ProtectedRoute>
            }
          />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
